/* eslint-disable jsdoc/require-jsdoc */
import type { Proxy } from '#~/agent/types.js';
import { toHexFromId } from '#~/api/utils.js';
import { nats } from '#~/nats/index.js';
import type { NewtsConnection } from '#~/newts/connection.js';
import { useUser } from '#~/user/hooks.js';
import { Avatar } from '@studio/design-system/avatar';
import { Arrow, Tooltip } from '@studio/design-system/tooltip';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { pick } from 'lodash-es';
import { useEffect, useState, type ReactElement } from 'react';
import { shallow } from 'zustand/shallow';

export const SessionsAvatars = ({ agents }: { agents: Proxy[] }): ReactElement => {
  const user = useUser((µ) => pick(µ, ['email', 'id', 'firstName']), shallow);
  const [userSessions, setUserSessions] = useState<
    {
      id: string;
      joinedAt: number;
      name: string;
      photo: T_Maybe<string>;
    }[]
  >([]);

  const [lastRefresh, setLastRefresh] = useState<number>(Date.now());

  useEffect(() => {
    const agentSessions = agents
      .flatMap((proxy) => {
        const reservations = proxy.state.control?.reservation;

        if (!reservations) {
          return [];
        }

        return Object.values(reservations);
      })
      .filter((a) => !!a)
      .sort((a, b) => {
        const aLastPing = a?.lastPing ?? 0;
        const bLastPing = b?.lastPing ?? 0;

        return bLastPing - aLastPing;
      });

    const livekitSessions = agents.flatMap((proxy) =>
      (nats.agentConnection(proxy.id) as NewtsConnection).remoteParticipants('hybrid')
    );

    const uniqueUserSessions: Record<
      string,
      {
        id: string;
        joinedAt: number;
        name: string;
        photo: T_Maybe<string>;
      }
    > = {};

    for (const session of agentSessions) {
      const { lastPing, userId } = session;

      if (!userId || !lastPing || userId.length === 0) {
        continue;
      }

      if (Date.now() - lastPing > 60 * 1000) {
        continue;
      }

      const userIdHex = toHexFromId(userId);

      if (!userIdHex) {
        continue;
      }

      if (uniqueUserSessions[userIdHex]) {
        continue;
      }

      if (session.isVisible === false) {
        continue;
      }

      uniqueUserSessions[userIdHex] = {
        id: userIdHex,
        name: session.userName!,
        photo: session.userProfilePhotoUrl,
        joinedAt: session.joinedAt
      };
    }

    for (const participants of livekitSessions) {
      for (const sessionId of participants.keys()) {
        const participant = participants.get(sessionId);

        if (participant?.metadata === undefined || participant?.metadata === '') {
          continue;
        }

        try {
          const meta = JSON.parse(participant.metadata!);

          if (meta.identity === undefined) {
            continue;
          }

          if (!meta.identity.visible) {
            continue;
          }

          const userId = meta.identity.userId.toLowerCase();

          if (uniqueUserSessions[userId]) {
            continue;
          }

          uniqueUserSessions[userId] = {
            id: userId,
            name: meta.identity.userName,
            photo: meta.identity.userPhoto,
            joinedAt: meta.identity.sessionJoinedAt
          };
        } catch (error) {
          // ignore errors from the outside
        }
      }
    }

    const userSessions = Object.values(uniqueUserSessions)
      .sort((a, b) => a.joinedAt - b.joinedAt)
      .filter((session) => session.id.toLowerCase() !== user.id.toLowerCase());

    setUserSessions(userSessions);
  }, [agents, user, lastRefresh]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastRefresh(Date.now());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  });

  return (
    <div className='group/all flex flex-row transition-[margin] ease-in-out hover:mr-[-14px]'>
      {userSessions.map(({ id, name, photo }) => (
        <div className='w-[16px] transition-[width] ease-in-out group-hover/all:w-[30px]' key={id}>
          <Tooltip
            trigger={
              <button type='button'>
                <Avatar name={name} outline='base-100' photo={photo} />
              </button>
            }
          >
            <div className='rounded-4 bg-[white] px-12 py-6 text-sm'>
              {name}
              <Arrow className='fill-[white]' fill='white' />
            </div>
          </Tooltip>
        </div>
      ))}
    </div>
  );
};
